import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GlossaryText } from "@/components/GlossaryText";
import { PostCard } from "@/components/PostCard";
import { BookCard } from "@/components/BookCard";
import { PostComposer, StarRating } from "@/components/PostComposer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Lock, ArrowLeft, Trash2, Save, X, Sparkles, Pencil, BookOpen } from "lucide-react";
import { Confetti } from "@/components/Confetti";
import { toast } from "sonner";

const APHORISMS = [
  "Bilgi, ruhun besinidir. — Platon",
  "Kendini bil. — Delphi Tapınağı",
  "Öğrenmek, hatırlamaktır. — Sokrates",
  "Bilgelik, hayranlıkla başlar. — Sokrates",
  "Erdem bilgidir. — Sokrates",
  "Okumak, ruhun yolculuğudur.",
];

export default function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, glossary } = useAuth();
  const [book, setBook] = useState(null);
  const [posts, setPosts] = useState([]);
  const [durum, setDurum] = useState("");
  const [puan, setPuan] = useState(0);
  const [notlar, setNotlar] = useState("");
  const [celebrate, setCelebrate] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [similar, setSimilar] = useState([]);
  const isStaff = ["admin", "moderator"].includes(user?.role);

  const load = useCallback(async () => {
    const [b, p] = await Promise.all([api.get(`/books/${id}`), api.get(`/books/${id}/posts`)]);
    setBook(b.data);
    api.get(`/books/${id}/similar`).then((r) => setSimilar(r.data)).catch(() => {});
    setPosts(p.data);
    const ub = b.data.benim_durumum;
    if (ub) { setDurum(ub.durum || ""); setPuan(ub.puan || 0); setNotlar(ub.ozel_notlar || ""); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveStatus = async (newDurum, newPuan) => {
    try {
      const { data } = await api.put("/user-books", { book_id: id, durum: newDurum, puan: newPuan });
      toast.success("Okuma durumun güncellendi.");
      if (data?.yeni_rozetler?.length) {
        setCelebrate({ rozet: data.yeni_rozetler[0], soz: APHORISMS[Math.floor(Math.random() * APHORISMS.length)] });
      }
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeStatus = async () => {
    await api.delete(`/user-books/${id}`);
    setDurum(""); setPuan(0); setNotlar("");
    toast.success("İşaretleme kaldırıldı.");
    load();
  };

  const saveNotes = async () => {
    await api.put("/user-books", { book_id: id, durum: durum || "Okuyacağım", ozel_notlar: notlar });
    toast.success("Gizli notun kaydedildi.");
  };

  const markPastRead = async () => {
    await api.put("/user-books", { book_id: id, durum: "Okudum", gecmis: true });
    toast.success("Geçmiş okumalarına eklendi (puan eklenmez).");
    load();
  };

  const deletePost = async (pid) => {
    await api.delete(`/posts/${pid}`);
    setPosts(posts.filter((x) => x.id !== pid));
    toast.success("Paylaşım silindi.");
  };

  const deleteBook = async () => {
    if (!window.confirm("Bu kitabı silmek istediğine emin misin?")) return;
    await api.delete(`/books/${id}`);
    toast.success("Kitap silindi.");
    navigate("/kitaplar");
  };

  const openEdit = () => {
    setForm({
      baslik: book.baslik || "", yazar: book.yazar || "", yayinevi: book.yayinevi || "",
      zorluk_seviyesi: book.zorluk_seviyesi || "Orta", etiketler: (book.etiketler || []).join(", "),
      kapak_resmi: book.kapak_resmi || "", ozet_abstract: book.ozet_abstract || "",
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      const payload = { ...form, etiketler: form.etiketler ? form.etiketler.split(",").map((t) => t.trim()).filter(Boolean) : [] };
      await api.put(`/books/${id}`, payload);
      toast.success("Kitap güncellendi."); setEditOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const genMeta = async () => {
    if (!form.baslik || !form.yazar) { toast.error("Başlık ve yazar gerekli."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/books/generate-meta", { baslik: form.baslik, yazar: form.yazar });
      setForm((f) => ({ ...f, zorluk_seviyesi: data.zorluk_seviyesi, etiketler: (data.etiketler || []).join(", ") }));
      toast.success("Zorluk ve etiketler önerildi.");
    } catch (e) { toast.error("Oluşturulamadı."); } finally { setAiLoading(false); }
  };
  const genAbstract = async () => {
    if (!form.baslik || !form.yazar) { toast.error("Başlık ve yazar gerekli."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/books/generate-abstract", { baslik: form.baslik, yazar: form.yazar });
      setForm((f) => ({ ...f, ozet_abstract: data.abstract }));
      toast.success("Abstract oluşturuldu.");
    } catch (e) { toast.error("Oluşturulamadı."); } finally { setAiLoading(false); }
  };

  if (!book) return <div className="max-w-7xl mx-auto px-6 py-12 text-muted-foreground">Yükleniyor...</div>;

  const durumRenk = { Okudum: "bg-sage text-white", Okuyorum: "bg-gold text-ink", Okuyacağım: "bg-[#8A94A6] text-white" };

  return (
    <>
    <Confetti fire={!!celebrate} />
    {celebrate && (
      <div className="fixed inset-0 z-[110] flex items-center justify-center bg-ink/60 p-4" data-testid="badge-celebration" onClick={() => setCelebrate(null)}>
        <div className="bg-surface rounded-2xl p-8 max-w-sm text-center shadow-2xl fade-up" onClick={(e) => e.stopPropagation()}>
          <div className="w-24 h-24 mx-auto rounded-full bg-gold/15 border-4 border-gold/40 flex items-center justify-center text-5xl animate-pulse">🏅</div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold mt-4">Yeni Rozet!</p>
          <h3 className="font-serif text-3xl text-ink mt-1">{celebrate.rozet.ad}</h3>
          <p className="text-sm text-ink-2 mt-2">{celebrate.rozet.aciklama}</p>
          <p className="font-serif italic text-lg text-terra mt-5 leading-snug">"{celebrate.soz}"</p>
          <Button onClick={() => setCelebrate(null)} className="mt-6 bg-terra hover:bg-terra-hover text-white rounded-full px-8">Harika!</Button>
        </div>
      </div>
    )}
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-terra mb-6">
        <ArrowLeft size={16} /> Geri
      </button>
      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        {/* Left sticky */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="w-full max-w-[240px] mx-auto lg:mx-0 aspect-square rounded-xl border border-line bg-ink flex items-center justify-center p-4 overflow-hidden">
            {book.kapak_resmi ? <img src={book.kapak_resmi} alt={book.baslik} className="w-full h-full object-cover rounded-lg" />
              : <span className="font-serif text-xl text-page text-center uppercase tracking-wide leading-tight">{book.baslik}</span>}
          </div>

          <div className="mt-5 border border-line rounded-xl p-4 bg-surface">
            <p className="text-sm font-medium text-ink mb-2">Okuma Durumu</p>
            <ToggleGroup type="single" value={durum} onValueChange={(v) => { if (v) { setDurum(v); saveStatus(v, puan); } }}
              className="grid grid-cols-3 gap-1" data-testid="status-toggle">
              {["Okudum", "Okuyorum", "Okuyacağım"].map((d) => (
                <ToggleGroupItem key={d} value={d} data-testid={`status-${d}`}
                  className={`text-xs rounded-md border border-line py-2 data-[state=on]:${durumRenk[d].split(" ")[0]} ${durum === d ? durumRenk[d] : "text-ink-2"}`}>
                  {d}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <div className="mt-4">
              <p className="text-sm font-medium text-ink mb-2">Puanın</p>
              <StarRating value={puan} onChange={(v) => { setPuan(v); saveStatus(durum || "Okudum", v); }} />
            </div>
            {durum && (
              <Button onClick={removeStatus} data-testid="remove-status" variant="outline" size="sm"
                className="mt-3 w-full border-line text-destructive hover:bg-destructive/10 gap-1">
                <X size={14} /> İşaretlemeyi Kaldır
              </Button>
            )}
          </div>

          {/* Gizli notlar */}
          <div className="mt-4 border border-line rounded-xl p-4 bg-surface">
            <div className="flex items-center gap-2 mb-2">
              <Lock size={15} className="text-terra" />
              <p className="text-sm font-medium text-ink">Gizli Çalışma Notların</p>
            </div>
            <p className="text-xs text-muted-foreground mb-2">Sadece sen görebilirsin.</p>
            <Textarea data-testid="private-notes" value={notlar} onChange={(e) => setNotlar(e.target.value)} rows={4}
              placeholder="Kişisel notlarını buraya yaz..." className="bg-surface-alt/50" />
            <Button onClick={saveNotes} data-testid="save-notes" size="sm"
              className="mt-2 w-full bg-ink hover:bg-ink-2 text-page rounded-md gap-1"><Save size={14} /> Notu Kaydet</Button>
            <Button onClick={markPastRead} data-testid="mark-past-read" size="sm" variant="outline"
              className="mt-2 w-full border-line gap-1"><BookOpen size={14} /> Daha önce okumuştum</Button>
          </div>

          {isStaff && (
            <div className="mt-4 flex gap-2">
              <Button onClick={openEdit} data-testid="edit-book-btn" variant="outline" size="sm" className="flex-1 border-line gap-1"><Pencil size={14} /> Düzenle</Button>
              <Button onClick={deleteBook} variant="outline" size="sm" data-testid="delete-book" className="border-line text-destructive hover:bg-destructive/10"><Trash2 size={15} /></Button>
            </div>
          )}
        </div>

        {/* Right details */}
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            {book.etiketler?.map((t) => (
              <Link key={t} to={`/kitaplar?etiket=${encodeURIComponent(t)}`} data-testid={`book-tag-${t}`}
                className="text-xs uppercase tracking-wider text-terra bg-terra/8 border border-terra/15 px-2.5 py-1 rounded-full hover:bg-terra hover:text-white transition-colors">{t}</Link>
            ))}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-ink tracking-tight leading-tight">{book.baslik}</h1>
          <p className="text-base text-ink-2 mt-1.5">
            <Link to={`/yazar/${encodeURIComponent(book.yazar)}`} data-testid="book-author-link" className="hover:text-terra hover:underline font-medium">{book.yazar}</Link>
            {book.yayinevi ? ` · ${book.yayinevi}` : ""}
          </p>

          <div className="flex flex-wrap items-center gap-5 mt-3 text-sm">
            <span className="text-ink-2"><span className="text-muted-foreground">Zorluk:</span> {book.zorluk_seviyesi}</span>
            {book.topluluk_puani > 0 && (
              <span className="flex items-center gap-1"><Star size={15} className="fill-gold text-gold" /> {book.topluluk_puani} <span className="text-muted-foreground">({book.puan_sayisi} puan)</span></span>
            )}
            <span className="text-ink-2"><span className="text-muted-foreground">Okuyan:</span> {book.okuyan_sayisi}</span>
          </div>

          {book.ozet_abstract && (
            <div className="mt-5 border-l-2 border-terra pl-4">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra mb-1.5">Abstract</p>
              <p className="text-ink-2 leading-relaxed text-sm"><GlossaryText text={book.ozet_abstract} glossary={glossary} /></p>
            </div>
          )}
          {book.kunye_bilgisi && <p className="text-sm text-muted-foreground mt-4">{book.kunye_bilgisi}</p>}

          <div className="mt-10">
            <h2 className="font-serif text-2xl text-ink mb-4">Topluluk Paylaşımları</h2>
            <PostComposer bookId={id} onCreated={load} />
            <div className="space-y-4 mt-6" data-testid="posts-list">
              {posts.length === 0 && <p className="text-muted-foreground text-sm">Henüz paylaşım yok. İlk yorumu/alıntıyı sen ekle!</p>}
              {posts.map((p) => <PostCard key={p.id} post={p} glossary={glossary} onDelete={deletePost} />)}
            </div>
          </div>

          {similar.length > 0 && (
            <div className="mt-10" data-testid="similar-books">
              <h2 className="font-serif text-2xl text-ink mb-4">Benzer Kitaplar</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {similar.map((b) => <BookCard key={b.id} book={b} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    {form && (
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-surface max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Kitabı Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Başlık</Label><Input data-testid="edit-bk-title" value={form.baslik} onChange={(e) => setForm({ ...form, baslik: e.target.value })} className="mt-1" /></div>
              <div><Label>Yazar</Label><Input data-testid="edit-bk-author" value={form.yazar} onChange={(e) => setForm({ ...form, yazar: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Yayınevi</Label><Input value={form.yayinevi} onChange={(e) => setForm({ ...form, yayinevi: e.target.value })} className="mt-1" /></div>
              <div><Label>Zorluk</Label>
                <Select value={form.zorluk_seviyesi} onValueChange={(v) => setForm({ ...form, zorluk_seviyesi: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Başlangıç">Başlangıç</SelectItem><SelectItem value="Orta">Orta</SelectItem><SelectItem value="İleri">İleri</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between"><Label>Etiketler (virgülle)</Label>
                <Button type="button" size="sm" variant="ghost" data-testid="edit-gen-meta" onClick={genMeta} disabled={aiLoading} className="text-terra gap-1 h-7"><Sparkles size={14} /> {aiLoading ? "..." : "AI ile Öner"}</Button>
              </div>
              <Input data-testid="edit-bk-tags" value={form.etiketler} onChange={(e) => setForm({ ...form, etiketler: e.target.value })} className="mt-1" placeholder="Stoacılık, Etik" />
            </div>
            <div><Label>Kapak Resmi URL</Label><Input value={form.kapak_resmi} onChange={(e) => setForm({ ...form, kapak_resmi: e.target.value })} className="mt-1" /></div>
            <div>
              <div className="flex items-center justify-between"><Label>Abstract</Label>
                <Button type="button" size="sm" variant="ghost" data-testid="edit-gen-abstract" onClick={genAbstract} disabled={aiLoading} className="text-terra gap-1 h-7"><Sparkles size={14} /> {aiLoading ? "..." : "AI ile Oluştur"}</Button>
              </div>
              <Textarea data-testid="edit-bk-abstract" value={form.ozet_abstract} onChange={(e) => setForm({ ...form, ozet_abstract: e.target.value })} className="mt-1" rows={4} />
            </div>
          </div>
          <DialogFooter><Button onClick={saveEdit} data-testid="save-edit-book" className="bg-terra hover:bg-terra-hover text-white">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}
